import React, { useState, useEffect, useCallback } from 'react';

import cx from 'classnames';
import {
  CountryCode,
  parsePhoneNumber,
  ENCLOSED_PARENTHESIS_REGEX,
  formatIncompletePhoneNumber,
  dropdownItems,
  isSupportedCountry,
} from 'lane-shared/helpers/phoneNumbers';

import { Dropdown, ValidatedInput } from 'components/form';
import { Flex } from 'components/layout';
import { S } from 'components/typography';

import styles from './VisitorPhoneInput.scss';

type Props = {
  className?: string;
  style?: React.CSSProperties;
  displayName?: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  validation?: {};
  isPristine?: boolean;
  isHidden?: boolean;
  isRequired?: boolean;
  testId?: string;
  isDisabled?: boolean;
  onRegionChange?: (regionCode: CountryCode) => void;
};

export default function VisitorPhoneInput({
  className,
  style,
  displayName,
  placeholder,
  value,
  onChange,
  validation,
  isPristine,
  isHidden = false,
  isRequired = false,
  testId,
  isDisabled = false,
  onRegionChange,
}: Props) {
  const browserLanguage = window.navigator.language;
  const browserRegion = new Intl.Locale(browserLanguage).region || '';
  const defaultRegion = isSupportedCountry(browserRegion)
    ? (browserRegion as CountryCode)
    : 'US';
  const [regionCode, setRegionCode] = useState<CountryCode>(defaultRegion);
  const [newlyInputtedNumber, setNewlyInputtedNumber] = useState(value || '');

  useEffect(() => {
    // load national phone number on initial render if passed
    if (value) {
      const number = parsePhoneNumber(value)?.nationalNumber;

      if (number) {
        setNewlyInputtedNumber(number.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegionChange = useCallback((country: CountryCode) => {
    onRegionChange?.(country);
  }, [onRegionChange]);

  useEffect(() => {
    if (value) {
      const country = parsePhoneNumber(value)?.country;

      if (country) {
        setRegionCode(country);
        handleRegionChange(country);
      }
    }

    if (!value) {
      setNewlyInputtedNumber(value);
    }
  }, [value, handleRegionChange]);

  /* Prevent formatting when deleting closing parenthesis */
  const formattedNumber = newlyInputtedNumber.match(ENCLOSED_PARENTHESIS_REGEX)
    ? newlyInputtedNumber
    : formatIncompletePhoneNumber(newlyInputtedNumber ?? '', regionCode);

  if (isHidden) return null;

  function handleChange(value: string) {
    setNewlyInputtedNumber(value);
    const phoneNumber = parsePhoneNumber(value, regionCode);

    if (phoneNumber) {
      onChange(phoneNumber.format('E.164'));
    } else {
      onChange(value);
    }
  }

  return (
    <div className={cx(styles.visitorPhoneInput, className)} style={style}>
      <S>
        {displayName}
        {isRequired ? <span className={styles.required}>*</span> : ''}
      </S>
      <Flex className={styles.form}>
        <div className={styles.dropdownWrapper}>
          <Dropdown
            items={dropdownItems}
            placeholder="Country"
            value={regionCode}
            onChange={code => {
              setRegionCode(code.value);
              handleRegionChange(code.value);
            }}
            className={styles.dropdown}
            disabled={isDisabled}
            testId="VisitorPhoneDropdown"
          />
        </div>
        <ValidatedInput
          testId={testId}
          className={styles.inputField}
          value={formattedNumber}
          onChange={handleChange}
          maxLength={16}
          type="tel"
          placeholder={placeholder}
          validation={validation}
          isPristine={isPristine}
          disabled={isDisabled}
        />
      </Flex>
    </div>
  );
}
